import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { getMemeDetailsVideoList } from '@opm/services';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';
import { PageInfoType } from '@op/types/common';

const getCommonVideoValue = (data: any) => {
  return [
    { key: 'views', valueKey: 'views' },
    { key: 'likes', valueKey: 'likes' },
    { key: 'comment', valueKey: 'comments' },
    { key: 'username', valueKey: 'author_name' },
    { key: 'cover', valueKey: 'video_cover' },
    { key: 'time', valueKey: 'release_time' },
    { key: 'channel', valueKey: 'channel' },
    { key: 'url', valueKey: 'video_url' },
    { key: 'engagement', valueKey: 'engagement' },
    { key: 'shares', valueKey: 'shares' },
    { key: 'video_cover', valueKey: 'video_cover' },
  ].reduce((pre, cur) => {
    const { key, valueKey } = cur;
    return {
      ...pre,
      [key]: data[valueKey],
    };
  }, data);
};

export function useMemeDetailsVideoList({
  filter,
  defaultPageInfo = { page: 1, page_size: 40 },
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
}) {
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'views',
  });
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({
    defaultPageInfo,
  });
  const [dataRef, data, setData] = useStateRef<any[][]>([]);
  const [operationRef, operation, setOperation] = useStateRef<'card' | 'table'>(
    'card',
  );
  const filterRef = useRef<any>(filter);

  function getListData(data: any[][]) {
    const result: Array<any> = [];
    let page = 1;
    data.every(item => {
      if (item) {
        result.push(...item);
        page = page + 1;
        return true;
      }
      return false;
    });
    return { page, result };
  }
  const [total, setTotal] = useState(0);
  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook();

  function resetData() {
    setData([]);
    resetPageInfo();
  }

  function changeSortBy(sortBy: SortBy<string>) {
    setSortBy(sortBy);
    resetData();
  }

  function changeKeyword(keyword: string) {
    setKeyword(keyword);
    resetData();
  }

  const params = useMemo(() => {
    if (filterRef?.current !== filter) {
      filterRef.current = filter;
      resetData();
    }
    return {
      ...filter,
      ...pageInfo,
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
    };
  }, [filter, pageInfo, operation]);
  const hasMoreVideo = useRef(false);
  function request() {
    if (!filter?.meme_title) return;
    wrapperCancelRequest(getMemeDetailsVideoList, params)
      .then(data => {
        const { list, total } = data || {};
        const realList = list?.map((item: any) => getCommonVideoValue(item));

        if (dataRef.current) {
          dataRef.current[pageInfo.page - 1] = realList;
          setData([...dataRef.current]);
        }
        setTotal(total || 0);
        hasMoreVideo.current = total > pageInfo.page * pageInfo.page_size;
      })
      .catch(() => {
        const { page } = getListData(dataRef.current || []);
        if (page > 0) {
          changePageInfoNoRender({ ...pageInfo, page: page - 1 });
        }
      });
  }

  useEffect(() => {
    const { page } = getListData(dataRef.current || []);
    if (pageInfo.page >= page) {
      request();
    }
  }, [params]);

  function download() {
    gotoDownload({
      key: 'MemeVideoDownload',
      fileName: `Meme_Videos_${DBO_TM.timeTitle(params)}`,
      filter: params,
    });
  }

  function changePageInfoData(localPageInfo: PageInfoType) {
    if (pageInfo.page_size !== localPageInfo.page_size) {
      setData([]);
    }
    changePageInfo(localPageInfo);
  }

  const nextPageChange = useCallback(() => {
    if (operationRef.current === 'table') {
      return;
    }

    if (loadingRef.current) {
      return;
    }

    if (!hasMoreVideo.current) {
      return;
    }

    nextPage();
  }, []);

  function changeOperation(value: 'card' | 'table') {
    setOperation(value);
    if (value === 'table') {
      changePageInfo({ ...pageInfo, page: 1 });
    } else {
      const { page } = getListData(dataRef.current || []);
      changePageInfoNoRender({ ...pageInfo, page: page - 1 });
    }
  }

  const cardRecords = useMemo(() => {
    return getListData(data || []).result;
  }, [data, pageInfo]);

  const tableRecords = useMemo(() => {
    return data?.[pageInfo.page - 1] || [];
  }, [data, pageInfo]);

  return {
    data,
    cardRecords,
    tableRecords,
    total,
    pageInfo,
    changePageInfo: changePageInfoData,
    nextPage: nextPageChange,
    loading,
    sortBy,
    keyword,
    setKeyword: changeKeyword,
    setSortBy: changeSortBy,
    download,
    operation,
    setOperation: changeOperation,
    resetData,
    loadingRef,
  };
}
